import { uploadFile } from '@/lib/upload-file';

// Uploads an expense receipt photo to S3 via the presigned-URL flow and returns
// the public, hosted file URL to store as the expense's `receipt_url`.
//
// Regression: GitHub issue #201 — the New Expense flow used the GET
// `/uploads/presign` endpoint, which only returns the raw S3 object key (e.g.
// `tenants/.../receipt.jpg`), and stored that key in `receipt_url`. The detail
// page then bound the bare key straight into an `<img src>`, so the browser
// resolved it against the admin origin (https://localhost:3002/...) and 404'd —
// both the thumbnail and the full-size modal showed a broken image. This mirrors
// the product/logo/social-image flow: it uploads via the presigned URL and
// returns the hosted `file_url` so the receipt is actually fetchable.

export async function uploadExpenseReceipt(
  file: File,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const { file_url } = await uploadFile(file, 'expense-receipt', fetchImpl);
  return file_url;
}

// Raised when a receipt upload was attempted but failed, so the caller can tell
// it apart from a create error and stop submission instead of silently saving a
// receipt-less expense (GitHub issue #202).
export class ReceiptUploadError extends Error {
  constructor() {
    super('Receipt upload failed');
    this.name = 'ReceiptUploadError';
  }
}

// Resolves the `receipt_url` to store on a new expense. Returns undefined when
// there's no file or the user has explicitly chosen to submit without one. When
// an upload is attempted and fails (non-OK presign OR a thrown S3 PUT), throws
// ReceiptUploadError so the caller aborts the create — the previous code
// swallowed the failure and submitted the expense without the receipt, leaving
// the user to believe it was attached (#202).
export async function resolveReceiptUrl(
  receiptFile: File | null,
  skipReceipt: boolean,
  upload: (file: File) => Promise<string> = uploadExpenseReceipt,
): Promise<string | undefined> {
  if (!receiptFile || skipReceipt) return undefined;
  try {
    return await upload(receiptFile);
  } catch {
    throw new ReceiptUploadError();
  }
}
